import type { UseCommunicationsRealtimeParams } from '../../types/realtime/use-communications-realtime.types';
import { isCommunicationsRealtimeEnabled } from 'src/modules/shared/realtime/communications/utils/release-gate.util';
import { useCurrentUser } from 'src/hooks/users';
import { useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ErrorReporter from 'src/monitoring/errorReporter';
import { useRealtimeTopicLifecycle } from 'src/modules/shared/realtime/hooks/provider-bounded/use-realtime-topic-lifecycle';
import { useCommunicationsQueryRecovery } from './use-communications-query-recovery';
import {
  buildFunnelTopic,
  buildConversationTopic,
  buildIdsKey,
  parseIdsKey,
} from '../../utils/realtime/communications-topics.util';
import { useRealtimeEventListener } from 'src/modules/shared/realtime/hooks/provider-bounded/use-realtime-event-listener';
import type { RealtimeEventMessage } from 'src/modules/shared/realtime/types/realtime.types';
import { applyCommunicationsRealtimePayload } from 'src/modules/shared/realtime/communications/utils/cache/realtime-update.util';
import { acceptRealtimeResourceVersion } from 'src/modules/shared/realtime/communications/utils/cache/event-order.util';
import { communicationsRealtimePayloadSchema } from 'src/modules/shared/realtime/communications/schemas/realtime.schema';
import { resourceChangedNotificationSchema } from 'src/modules/shared/realtime/communications/schemas/resource-changed.schema';
import { recoverCanonicalConversation } from 'src/modules/shared/communications/services/canonical-conversation-recovery.service';
import { CommunicationsResourceChangedNotification } from 'src/api/generated/models/CommunicationsResourceChangedNotification';

const UNAVAILABLE_RECOVERY_INTERVAL_MS = 30_000;
const EMPTY_IDS: readonly number[] = [];

/**
 * Subscribes the current view to Communications socket topics and reconciles
 * socket payloads with React Query caches.
 *
 * @param params - Funnel and conversation identifiers that should receive live updates.
 */
export const useCommunicationsRealtime = (
  params: UseCommunicationsRealtimeParams
): void => {
  const {
    funnelId,
    funnelIds = EMPTY_IDS,
    conversationId,
    conversationIds = EMPTY_IDS,
  } = params;
  const queryClient = useQueryClient();
  const { currentUser } = useCurrentUser();
  const processedEventIdsRef = useRef<Set<string>>(new Set());
  const funnelIdsKey = buildIdsKey([funnelId, ...funnelIds]);
  const conversationIdsKey = buildIdsKey([conversationId, ...conversationIds]);

  const communicationsEnabled = isCommunicationsRealtimeEnabled();
  const topics = useMemo(() => {
    if (!communicationsEnabled) return [];
    return [
      ...parseIdsKey(funnelIdsKey).map(buildFunnelTopic),
      ...parseIdsKey(conversationIdsKey).map(buildConversationTopic),
    ];
  }, [communicationsEnabled, conversationIdsKey, funnelIdsKey]);

  const invalidateActiveCommunicationsQueries =
    useCommunicationsQueryRecovery();

  const handleRealtimeEvent = useCallback(
    (message: RealtimeEventMessage) => {
      if (!topics.includes(message.topic)) {
        return;
      }

      const compact = resourceChangedNotificationSchema.safeParse(
        message.payload.data
      );
      const parseResult = compact.success
        ? compact
        : communicationsRealtimePayloadSchema.safeParse(message.payload.data);

      if (!parseResult.success) {
        ErrorReporter.sendException(parseResult.error);
        invalidateActiveCommunicationsQueries();
        return;
      }

      const payload = parseResult.data;
      if (
        !currentUser?.tenantId ||
        payload.tenantId !== currentUser.tenantId ||
        (message.topic !==
          buildConversationTopic(payload.funnelConversationId) &&
          message.topic !==
            (payload.funnelId ? buildFunnelTopic(payload.funnelId) : undefined))
      ) {
        return;
      }
      if (processedEventIdsRef.current.has(payload.eventId)) {
        return;
      }

      processedEventIdsRef.current.add(payload.eventId);
      if (processedEventIdsRef.current.size > 500) {
        const [oldestEventId] = processedEventIdsRef.current;
        if (oldestEventId) {
          processedEventIdsRef.current.delete(oldestEventId);
        }
      }

      if (
        payload.eventType ===
        CommunicationsResourceChangedNotification.eventType
          .COMMUNICATIONS_RESOURCE_CHANGED
      ) {
        // Authenticated HTTP views, not a missing resource snapshot, own recovery.
        // All active consumers share the existing debounce and QueryClient.
        invalidateActiveCommunicationsQueries(
          payload.resourceType !==
            CommunicationsResourceChangedNotification.resourceType.CONVERSATION
        );
        if (
          payload.resourceType ===
            CommunicationsResourceChangedNotification.resourceType
              .CONVERSATION &&
          // Retain the target in the same owner as full snapshots: an older
          // delivery must not cancel the canonical read for this notification.
          acceptRealtimeResourceVersion(queryClient, payload)
        ) {
          void recoverCanonicalConversation(
            queryClient,
            payload.funnelConversationId,
            payload.tenantId,
            payload.occurredAt
          );
        }
        return;
      }

      // Unambiguous socket payloads are authoritative. GET reads can briefly lag
      // realtime writes, so invalidation is reserved for recovery paths.
      if (applyCommunicationsRealtimePayload(queryClient, payload)) {
        invalidateActiveCommunicationsQueries(false);
      }
    },
    [
      currentUser?.tenantId,
      invalidateActiveCommunicationsQueries,
      queryClient,
      topics,
    ]
  );

  useRealtimeTopicLifecycle({
    topics,
    onRecover: invalidateActiveCommunicationsQueries,
    unavailableRecoveryIntervalMs: UNAVAILABLE_RECOVERY_INTERVAL_MS,
  });
  useRealtimeEventListener(handleRealtimeEvent);
};
